import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import Navbar from '../components/Navbar';
import { Stethoscope, ShieldCheck, CurrencyInr, Clock, Heart, CheckCircle } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DoctorApplication() {
  const navigate = useNavigate();
  const [specialties, setSpecialties] = useState([]);
  const [subspecialties, setSubspecialties] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    specialty: '',
    subspecialty: '',
    qualifications: '',
    registration_number: '',
    years_experience: '',
    current_hospital: '',
    bio: '',
  });

  useEffect(() => {
    axios.get(`${API}/specialties`).then(res => {
      setSpecialties(res.data.specialties || []);
      setSubspecialties(res.data.subspecialties || {});
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'specialty') {
      setFormData({ ...formData, specialty: value, subspecialty: '' });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/doctors/apply`, {
        ...formData,
        years_experience: parseInt(formData.years_experience, 10) || 0,
      });
      toast.success('Application submitted successfully!');
      setSubmitted(true);
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Application failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    {
      icon: CurrencyInr,
      title: 'Earn ₹1,500 - ₹2,800 per Case',
      description: 'Get paid promptly for every case opinion you submit. Set your own pace.',
    },
    {
      icon: Clock,
      title: 'Work on Your Schedule',
      description: 'Review cases on weekends, evenings, or whenever you have time.',
    },
    {
      icon: Heart,
      title: 'Make a Real Difference',
      description: 'Help patients avoid unnecessary surgeries and get the right treatment.',
    },
    {
      icon: ShieldCheck,
      title: 'Verified Patient Cases',
      description: 'Only well-documented cases with complete history and reports reach you.',
    },
  ];

  if (submitted) {
    return (
      <div className="min-h-screen bg-surface-secondary" data-testid="doctor-application-success">
        <Navbar />
        <div className="flex items-center justify-center min-h-screen pt-16 px-4">
          <div className="w-full max-w-md card text-center">
            <CheckCircle size={64} weight="duotone" className="text-primary mx-auto mb-4" />
            <h2 className="font-manrope text-2xl font-bold text-primary mb-3">Application Received!</h2>
            <p className="text-secondary mb-6">
              Thank you for applying to join SecondDoctorOpinion.com. Our admin team will verify your credentials and notify you via email within 2 business days.
            </p>
            <Link to="/" className="btn-primary inline-block">Back to Home</Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface-secondary" data-testid="doctor-application-page">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-12 px-4 sm:px-6 lg:px-8 bg-surface">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary/10 mb-4">
            <Stethoscope size={32} weight="duotone" className="text-primary" />
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-accent font-semibold mb-2">For Specialists</p>
          <h1 className="font-spectral text-4xl sm:text-5xl font-bold text-primary tracking-tight leading-tight mb-4">
            Join Our Panel of Expert Specialists
          </h1>
          <p className="text-lg text-secondary max-w-2xl mx-auto leading-relaxed">
            List your credentials, get verified, and start providing second medical opinions to Indian patients online. Earn meaningfully while practicing medicine on your terms.
          </p>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 bg-surface-secondary">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((b, idx) => {
              const Icon = b.icon;
              return (
                <div key={idx} className="card text-center" data-testid={`benefit-${idx}`}>
                  <Icon size={36} weight="duotone" className="text-primary mx-auto mb-3" />
                  <h3 className="font-manrope text-base font-semibold text-primary mb-2">{b.title}</h3>
                  <p className="text-sm text-secondary leading-relaxed">{b.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Application Form */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 pb-20">
        <div className="max-w-3xl mx-auto">
          <div className="card">
            <h2 className="font-manrope text-2xl font-bold text-primary mb-2">Apply Now</h2>
            <p className="text-sm text-secondary mb-6">
              Fill in your credentials. Our admin team verifies your registration with MCI/SMC/NMC and approves applications within 2 business days.
            </p>

            <form onSubmit={handleSubmit} className="space-y-4" data-testid="doctor-application-form">
              {/* Personal Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Full Name with Title *</label>
                  <input
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Dr. Rajesh Mehta"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="doctor-name-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Specialty *</label>
                  <select
                    name="specialty"
                    value={formData.specialty}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="doctor-specialty-select"
                  >
                    <option value="">Select Specialty</option>
                    {specialties.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {specialties.length > 0 && (
                    <p className="text-xs text-secondary mt-1">{specialties.length} specialties available</p>
                  )}
                </div>
              </div>

              {formData.specialty && (subspecialties[formData.specialty] || []).length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">
                    Subspecialty / Super-specialty <span className="text-xs text-secondary font-normal">(optional, helps with case routing)</span>
                  </label>
                  <select
                    name="subspecialty"
                    value={formData.subspecialty}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="doctor-subspecialty-select"
                  >
                    <option value="">Generalist in {formData.specialty}</option>
                    {(subspecialties[formData.specialty] || []).map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              )}

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Email *</label>
                  <input
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="doctor-email-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Mobile Number *</label>
                  <input
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+91 98765 43210"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="doctor-phone-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Create Password *</label>
                <input
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  minLength={6}
                  placeholder="Minimum 6 characters"
                  className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  required
                  data-testid="doctor-password-input"
                />
              </div>

              {/* Credentials */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Qualifications *</label>
                  <input
                    name="qualifications"
                    value={formData.qualifications}
                    onChange={handleChange}
                    placeholder="MBBS, MD, DM (Cardiology)"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="doctor-qualifications-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Medical Council Registration No. *</label>
                  <input
                    name="registration_number"
                    value={formData.registration_number}
                    onChange={handleChange}
                    placeholder="e.g., MMC-12345"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="doctor-registration-input"
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Years of Experience *</label>
                  <input
                    name="years_experience"
                    type="number"
                    min="0"
                    value={formData.years_experience}
                    onChange={handleChange}
                    placeholder="e.g., 15"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    required
                    data-testid="doctor-experience-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-secondary mb-1">Current Hospital / Clinic</label>
                  <input
                    name="current_hospital"
                    value={formData.current_hospital}
                    onChange={handleChange}
                    placeholder="Optional"
                    className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                    data-testid="doctor-hospital-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-secondary mb-1">Brief Bio (Optional)</label>
                <textarea
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Areas of expertise, notable publications, fellowships, etc."
                  className="w-full px-4 py-2 border border-primary/20 rounded-lg bg-surface-secondary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  data-testid="doctor-bio-input"
                />
              </div>

              <div className="bg-surface-secondary rounded-lg p-4 text-xs text-secondary">
                <strong>What happens next?</strong> Our admin team will verify your medical registration with the relevant Medical Council. Once approved (typically within 2 business days), you'll receive an email and can start receiving case assignments in your specialty.
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full btn-primary py-3 disabled:opacity-50"
                data-testid="doctor-submit-button"
              >
                {loading ? 'Submitting...' : 'Submit Application for Review'}
              </button>

              <p className="text-xs text-center text-secondary">
                Already approved? <Link to="/login" className="text-primary font-semibold hover:underline">Login here</Link>
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
